import { useEffect, useRef, useState } from "react";
import {
  createQuoteAttachment,
  downloadCaseMailAttachment,
  loadCaseQuoteDocument,
  previewCaseMailAttachment,
  uploadCaseMailAttachment,
  type CaseMailAttachment,
  type CaseQuoteDocument,
} from "./api";

const sizeLabel = (size: number) =>
  size >= 1024 * 1024
    ? `${(size / 1024 / 1024).toFixed(1)} MiB`
    : `${Math.ceil(size / 1024)} KiB`;

async function download(file: CaseMailAttachment) {
  const result = await downloadCaseMailAttachment(file);
  const url = URL.createObjectURL(result.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = result.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

type PreviewKind = "image" | "pdf" | "text";

function previewKind(contentType: string): PreviewKind | null {
  const type = contentType.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  if (["image/png", "image/jpeg", "image/gif", "image/webp"].includes(type)) return "image";
  if (type === "application/pdf") return "pdf";
  if (["text/plain", "text/csv", "text/tab-separated-values", "text/markdown", "application/json", "application/xml", "text/xml"].includes(type)) return "text";
  return null;
}

type AttachmentPreview = {
  file: CaseMailAttachment;
  kind: PreviewKind;
  loading: boolean;
  url?: string;
  text?: string;
  error?: string;
};

export function MailAttachmentList({ files, separated = false }: { files?: CaseMailAttachment[]; separated?: boolean }) {
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<AttachmentPreview | null>(null);
  const previewRequest = useRef(0);

  useEffect(() => () => { previewRequest.current += 1; }, []);
  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview?.url]);

  function closePreview() {
    previewRequest.current += 1;
    setPreview(null);
  }

  async function openPreview(file: CaseMailAttachment) {
    const kind = previewKind(file.contentType);
    if (!kind) return;
    const request = ++previewRequest.current;
    setPreview({ file, kind, loading: true });
    try {
      const result = await previewCaseMailAttachment(file);
      if (kind === "text") {
        let value = await result.blob.text();
        if (file.contentType.split(";", 1)[0]?.trim().toLowerCase() === "application/json") {
          try { value = JSON.stringify(JSON.parse(value), null, 2); } catch { /* show original text */ }
        }
        if (previewRequest.current === request) setPreview({ file, kind, loading: false, text: value });
        return;
      }
      const url = URL.createObjectURL(result.blob);
      if (previewRequest.current === request) setPreview({ file, kind, loading: false, url });
      else URL.revokeObjectURL(url);
    } catch (reason) {
      if (previewRequest.current === request) setPreview({
        file, kind, loading: false,
        error: reason instanceof Error ? reason.message : String(reason),
      });
    }
  }

  if (!files?.length) return null;
  return (
    <div className={`case-mail-files${separated ? " case-mail-files--separated" : ""}`}>
      <ul>
        {files.map((file) => (
          <li key={file.id}>
            <span className="case-mail-file-name">{file.filename}</span>
            <small>
              {sizeLabel(file.byteSize)}
              {file.quoteVersion ? ` · 报价 v${file.quoteVersion}` : ""}
            </small>
            {previewKind(file.contentType) && <button type="button" onClick={() => void openPreview(file)}>预览</button>}
            <button type="button" onClick={() => {
              setError("");
              void download(file).catch((reason) => setError(String(reason.message ?? reason)));
            }}>下载</button>
          </li>
        ))}
      </ul>
      {error && <p role="alert">{error}</p>}
      {preview && <div className="case-attachment-preview-overlay" onClick={(event) => {
        if (event.target === event.currentTarget) closePreview();
      }} onKeyDown={(event) => { if (event.key === "Escape") closePreview(); }}>
        <section role="dialog" aria-modal="true" aria-label={`预览附件 ${preview.file.filename}`} className="case-attachment-preview-dialog">
          <header><div><strong>附件预览</strong><span>{preview.file.filename}</span></div>
            <button type="button" aria-label="关闭附件预览" onClick={closePreview}>×</button></header>
          <div className="case-attachment-preview-body">
            {preview.loading && <p>正在加载预览…</p>}
            {preview.error && <p role="alert">{preview.error}</p>}
            {!preview.loading && !preview.error && preview.kind === "image" && preview.url &&
              <img src={preview.url} alt={preview.file.filename} />}
            {!preview.loading && !preview.error && preview.kind === "pdf" && preview.url &&
              <iframe src={preview.url} title={preview.file.filename} />}
            {!preview.loading && !preview.error && preview.kind === "text" &&
              <pre>{preview.text}</pre>}
          </div>
          <footer><button type="button" onClick={() => {
            setError("");
            void download(preview.file).catch((reason) => setError(String(reason.message ?? reason)));
          }}>下载</button><button type="button" className="case-primary" onClick={closePreview}>关闭</button></footer>
        </section>
      </div>}
    </div>
  );
}

type Item = {
  key: string;
  name: string;
  source?: File;
  file?: CaseMailAttachment;
  error?: string;
  uploading: boolean;
};

export function CaseMailAttachments({ caseId, disabled, canReadQuote, onChange, onBlockedChange }: {
  caseId: string;
  disabled: boolean;
  canReadQuote: boolean;
  onChange: (files: CaseMailAttachment[]) => void;
  onBlockedChange: (blocked: boolean) => void;
}) {
  const [items, setItems] = useState<Item[]>([]);
  const [notice, setNotice] = useState("");
  const [quote, setQuote] = useState<CaseQuoteDocument | null | undefined>(undefined);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const alive = useRef(true);
  const inFlight = useRef(false);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  useEffect(() => {
    onChange(items.flatMap((item) => item.file ? [item.file] : []));
    onBlockedChange(quoteBusy || items.some((item) => item.uploading || Boolean(item.error)));
  }, [items, quoteBusy, onChange, onBlockedChange]);

  const total = items.reduce((sum, item) => sum + (item.file?.byteSize ?? item.source?.size ?? 0), 0);
  const working = quoteBusy || items.some((item) => item.uploading);

  async function upload(item: Item) {
    if (!item.source) return;
    setItems((rows) => rows.map((row) => row.key === item.key ? { ...row, uploading: true, error: undefined } : row));
    try {
      const file = await uploadCaseMailAttachment(caseId, item.source);
      if (alive.current) setItems((rows) => rows.map((row) => row.key === item.key ? { ...row, file, uploading: false } : row));
    } catch (reason) {
      if (alive.current) setItems((rows) => rows.map((row) => row.key === item.key ? {
        ...row,
        uploading: false,
        error: reason instanceof Error ? reason.message : String(reason),
      } : row));
    }
  }

  async function choose(files: File[]) {
    if (inFlight.current || disabled) return;
    if (items.length + files.length > 10) { setNotice("每封邮件最多 10 个附件"); return; }
    if (files.some((file) => !file.size || file.size > 10 * 1024 * 1024)) { setNotice("请选择非空文件，单文件不能超过 10 MiB"); return; }
    if (total + files.reduce((sum, file) => sum + file.size, 0) > 18 * 1024 * 1024) { setNotice("附件合计不能超过 18 MiB（含正文图片）"); return; }
    setNotice("");
    inFlight.current = true;
    const added = files.map((source) => ({ key: crypto.randomUUID(), name: source.name, source, uploading: true }));
    setItems((rows) => [...rows, ...added]);
    try {
      for (const item of added) {
        if (!alive.current) break;
        await upload(item);
      }
    } finally { inFlight.current = false; }
  }

  async function openQuote() {
    setQuoteBusy(true);
    setNotice("");
    try {
      const response = await loadCaseQuoteDocument(caseId);
      if (!alive.current) return;
      setQuote(response.versions[0] ?? (response.document.id ? response.document : null));
    } catch (reason) {
      if (alive.current) setNotice(reason instanceof Error ? reason.message : String(reason));
    } finally { if (alive.current) setQuoteBusy(false); }
  }

  async function addQuote() {
    if (!quote?.id || quoteBusy || quote.status === "DRAFT") return;
    if (items.some((item) => item.file?.quoteDocumentId === quote.id)) { setNotice("该报价单已在附件列表中"); return; }
    setQuoteBusy(true);
    setNotice("");
    try {
      const file = await createQuoteAttachment(caseId, quote.id);
      if (!alive.current) return;
      if (total + file.byteSize > 18 * 1024 * 1024) throw new Error("附件合计不能超过 18 MiB");
      setItems((rows) => [...rows, { key: file.id, name: file.filename, file, uploading: false }]);
      setQuote(undefined);
    } catch (reason) {
      if (alive.current) setNotice(reason instanceof Error ? reason.message : String(reason));
    } finally { if (alive.current) setQuoteBusy(false); }
  }

  return <section className="case-mail-attachments" aria-label="邮件附件">
    <div className="case-mail-attachment-actions"><strong>附件</strong>
      <button type="button" disabled={disabled || working || items.length >= 10} onClick={() => input.current?.click()}>添加附件</button>
      {canReadQuote && <button type="button" disabled={disabled || working || items.length >= 10} onClick={() => void openQuote()}>添加报价单</button>}
      <input ref={input} type="file" multiple hidden aria-label="上传邮件附件" disabled={disabled || working}
        onChange={(event) => { const files = [...(event.target.files ?? [])]; event.target.value = ""; void choose(files); }} />
      <small>最多 10 个，单文件 10 MiB，合计 18 MiB（含正文图片）</small>
    </div>
    <ul className="case-mail-upload-list">{items.map((item) => <li key={item.key}>
      <div>{item.file ? <MailAttachmentList files={[item.file]} /> : <span>{item.name}</span>}
        {item.uploading && <small>上传中…</small>}{item.error && <span role="alert">{item.error}</span>}</div>
      {item.error && <button type="button" disabled={disabled || working} onClick={() => void upload(item)}>重试</button>}
      <button type="button" aria-label={`移除 ${item.name}`} disabled={disabled || item.uploading || quoteBusy}
        onClick={() => setItems((rows) => rows.filter((row) => row.key !== item.key))}>移除</button>
    </li>)}</ul>
    {notice && <p role="alert">{notice}</p>}
    {quote !== undefined && <div className="case-quote-attachment-overlay" onKeyDown={(event) => {
      if (event.key === "Escape" && !quoteBusy) setQuote(undefined);
    }}>
      <section role="dialog" aria-modal="true" aria-label="添加最新报价单" className="case-quote-attachment-dialog">
        <header><strong>添加最新报价单</strong><button type="button" aria-label="关闭报价单预览" disabled={quoteBusy} onClick={() => setQuote(undefined)}>×</button></header>
        <p>仅可将本案件的最新正式报价版本生成 PDF 并添加到附件。</p>
        {quote ? <div className="case-quote-attachment-preview">
          <div className="case-quote-attachment-meta">
            <strong>{quote.customerQuoteNumber ?? `${quote.prefix}-${quote.quoteNumber}`}</strong>
            <span>{quote.status === "DRAFT" ? "待正式保存" : "正式版本"}</span>
            <span>报价日期：{quote.quoteDate}</span><span>有效期：{quote.validUntil}</span>
            <span>客户：{quote.customerName || "—"}</span><span>组织：{quote.organization || "—"}</span>
            <span>联系人：{quote.contact || "—"}</span><span>国家：{quote.country || "—"}</span>
            <span>付款条件：{quote.paymentTerms || "—"}</span><span>币种：{quote.currency}</span>
            <span className="case-quote-attachment-wide">收货地址：{quote.shippingAddress || "—"}</span>
            <span className="case-quote-attachment-wide">账单地址：{quote.billingAddress || "—"}</span>
          </div>
          <div className="case-quote-attachment-table-wrap"><table><thead><tr><th>产品</th><th>Cat No</th><th>CAS</th><th>纯度</th><th>数量</th><th>规格</th><th>单价</th><th>货期</th><th>小计</th></tr></thead>
            <tbody>{quote.lines.map((line, index) => <tr key={line.id ?? `${line.productName}-${index}`}>
              <td>{line.productName}</td><td>{line.catalogNumber || "—"}</td><td>{line.casNo || "—"}</td><td>{line.purity || "—"}</td><td>{line.quantity}</td>
              <td>{[line.specValue, line.specUnit].filter(Boolean).join(" ") || line.specification || "—"}</td>
              <td>{line.currency} {line.unitPrice || "—"}</td><td>{line.leadTime || "—"}</td>
              <td>{line.lineSubtotal === null ? "—" : `${line.currency} ${line.lineSubtotal}`}</td>
            </tr>)}</tbody>
          </table></div>
          <div className="case-quote-attachment-totals"><span>运费：{quote.currency} {quote.shippingFee}</span><span>手续费：{quote.currency} {quote.handlingFee}</span><strong>总额：<span aria-label="报价总额">{quote.omitTotal ? "" : `${quote.currency} ${quote.total ?? "—"}`}</span></strong></div>
          {quote.status === "DRAFT" && <p role="alert">这是升级前遗留的未正式保存记录，请先在报价单中保存为正式版本。</p>}
        </div> : <p>当前案件还没有报价单，请先创建正式报价单。</p>}
        {notice && <p role="alert">{notice}</p>}
        <footer><button type="button" disabled={quoteBusy} onClick={() => setQuote(undefined)}>取消</button>
          <button type="button" className="case-primary" disabled={!quote?.id || quote.status === "DRAFT" || quoteBusy || items.length >= 10}
            onClick={() => void addQuote()}>{quoteBusy ? "正在生成 PDF…" : "确认添加该报价单到附件"}</button></footer>
      </section>
    </div>}
  </section>;
}
